import { buildScreeningWireIdMap } from '../config/outbound-screening.config'
import { useFlowStore } from '../store'
import { type AiAgentV2NodeData, FlowNodeTypes } from '../types'
import { isAiAgentOrActionNode } from '../utils/ai-agent-utils'
import type {
  FlowRunEvent,
  NodeChunkCreatedEvent,
  SystemChunkCreatedEvent,
  SystemChunkMetadata,
} from './types'

// Screening sends a human label ("Identify contact"); collect-input sends the raw
// variable name as both label and id, so only surface a name when the two differ.
export const getSkillStartedDetail = (metadata?: SystemChunkMetadata | null): string | null => {
  const label = metadata?.label
  return label && label !== metadata?.task ? label : null
}

// Helper type for chat interface events
export type ChatEvent = NodeChunkCreatedEvent | SystemChunkCreatedEvent

// Type guards
export const isNodeChunkCreatedEvent = (event: FlowRunEvent): event is NodeChunkCreatedEvent =>
  event.event_name === 'NODE_CHUNK_CREATED'

export const isSystemChunkCreatedEvent = (event: FlowRunEvent): event is SystemChunkCreatedEvent =>
  event.event_name === 'SYSTEM_CHUNK_CREATED'

export const isChatEvent = (event: FlowRunEvent): event is ChatEvent =>
  isNodeChunkCreatedEvent(event) || isSystemChunkCreatedEvent(event)

// Utility function to filter FlowRunEvent array to only chat-relevant events
export const filterChatEvents = (events: FlowRunEvent[]): ChatEvent[] => events.filter(isChatEvent)

// Utility function to separate NodeChunkCreatedEvent and SystemChunkCreatedEvent
export const separateEventsByType = (events: FlowRunEvent[]) => {
  const nodeChunkEvents: NodeChunkCreatedEvent[] = []
  const systemChunkEvents: SystemChunkCreatedEvent[] = []

  events.forEach((event) => {
    if (isNodeChunkCreatedEvent(event)) {
      nodeChunkEvents.push(event)
    } else if (isSystemChunkCreatedEvent(event)) {
      systemChunkEvents.push(event)
    }
  })

  return { nodeChunkEvents, systemChunkEvents }
}

export interface ChosenEdge {
  sourceNodeId: string
  targetNodeId: string
  sourceHandle: string
  chosenPath: string
}

// Run events carry WIRE node ids. The outbound-screening composite ships as
// two wire nodes and only the dial half shares the composite's canvas id —
// remap the screening half so its execution and outcome edges match the
// canvas. Two feeders, because the persisted screening id is only derivable
// from the canvas for flows saved since the deterministic mint landed:
//  1. the mint map (covers current flows, including single-edge highlights)
//  2. the run's own lineage — the dial half's `answered` transition targets
//     the screening half, so map that target back onto the composite
//     (covers flows published before deterministic ids).
// The internal `answered` edge itself then keys as a self-reference that
// matches no canvas edge, which is correct: it is invisible inside the box.
export const buildCanvasIdResolver = (
  chosenEdges: ChosenEdge[] = [],
): ((wireNodeId: string) => string) => {
  const { flowInstance } = useFlowStore.getState()

  if (!flowInstance) return (wireNodeId: string) => wireNodeId

  const { getNode, getNodes } = flowInstance
  const wireToCanvas = buildScreeningWireIdMap(getNodes())
  chosenEdges.forEach((edge) => {
    if (edge.sourceHandle !== 'answered') return
    if (getNode(edge.sourceNodeId)?.type !== FlowNodeTypes.outbound_screening) return
    wireToCanvas.set(edge.targetNodeId, edge.sourceNodeId)
  })
  return (id: string) => wireToCanvas.get(id) ?? id
}

// Returns the wire->canvas id mapper, for callers that also address the canvas by run id.
export const highlightNodeAndEdges = (
  executedNodeIds: string[],
  chosenEdges: ChosenEdge[],
): ((wireNodeId: string) => string) => {
  const { flowInstance } = useFlowStore.getState()

  if (!flowInstance) return (wireNodeId: string) => wireNodeId

  const { setNodes, setEdges, getNode } = flowInstance
  const toCanvasId = buildCanvasIdResolver(chosenEdges)

  // Update nodes with execution state using spread operator
  const executedIdSet = new Set(executedNodeIds.map(toCanvasId))
  setNodes((nodes) =>
    nodes.map((node) => {
      const shouldHighlight = executedIdSet.has(node.id)
      const previousHighlight = node.data?.meta?.shouldHighlight
      if (previousHighlight === shouldHighlight) return node

      return {
        ...node,
        data: {
          ...node.data,
          meta: {
            ...(node.data.meta ?? {}),
            shouldHighlight,
          },
        },
      }
    }),
  )

  // Precompute normalized chosen-edge keys for constant-time lookups
  const chosenEdgeKeySet = new Set<string>()
  chosenEdges.forEach((chosenEdge) => {
    const sourceId = toCanvasId(chosenEdge.sourceNodeId)
    const targetId = toCanvasId(chosenEdge.targetNodeId)
    const sourceNode = getNode(sourceId)
    let { sourceHandle } = chosenEdge
    if (sourceNode && isAiAgentOrActionNode(sourceNode.type)) {
      const intent = (sourceNode.data?.config as AiAgentV2NodeData['config'])?.intents?.find(
        (intentItem) => intentItem.intent_name === chosenEdge.chosenPath,
      )
      if (intent) {
        sourceHandle = intent.id
      }
    }
    chosenEdgeKeySet.add(`${sourceId}|${targetId}|${String(sourceHandle)}`)
  })

  // Update edges with chosen path state using precomputed key set
  setEdges((edges) =>
    edges.map((edge) => {
      if (!edge.data) return edge

      const edgeKey = `${edge.source}|${edge.target}|${String(edge.sourceHandle)}`
      const isChosenPath = chosenEdgeKeySet.has(edgeKey)

      if (edge.data.isChosenPath === isChosenPath) return edge

      return {
        ...edge,
        data: {
          ...edge.data,
          isChosenPath,
        },
      }
    }),
  )

  return toCanvasId
}

// Legacy function for backward compatibility - single node/edge highlighting
export const highlightSingleNodeAndEdge = (
  currentNodeUuid?: string,
  chosenPath?: string,
  previousNodeUuid?: string,
) => {
  const executedNodeIds = currentNodeUuid ? [currentNodeUuid] : []
  const chosenEdges =
    previousNodeUuid && currentNodeUuid && chosenPath
      ? [
          {
            sourceNodeId: previousNodeUuid,
            targetNodeId: currentNodeUuid,
            sourceHandle: chosenPath,
            chosenPath,
          },
        ]
      : []

  return highlightNodeAndEdges(executedNodeIds, chosenEdges)
}

// Helper to format timestamp for display
export const formatEventTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp)
  return `${date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })}:${date.getMilliseconds().toString().padStart(3, '0')}`
}

export const isMediaSkill = (skillType: string) =>
  ['image_generation', 'text_to_speech', 'audio_generation'].includes(skillType)
